<template>
    <van-popup :show="props.show" position="center" :style="{ width: '85%' }" round @close="handleClose"
        @update:show="handleUpdateShow">
        <div class="add-student-modal">
            <!-- 标题 -->
            <div class="modal-header">
                <text class="modal-title">{{ props.mode === 'add' ? '新增学生' : '修改学生' }}</text>
            </div>

            <!-- 表单内容 -->
            <div class="modal-content">
                <van-form @submit="handleSubmit" class="form-container">
                    <!-- 学校 -->
                    <div class="form-item">
                        <div class="form-label">学校</div>
                        <div class="form-input" @click="showSchoolPicker = true">
                            <span class="input-text" :class="{ placeholder: !formData.school }">
                                {{ formData.school ? formData.school.name : '请输入或选择学校' }}
                            </span>
                            <van-icon name="search" class="input-icon" />
                        </div>
                    </div>

                    <!-- 年级 -->
                    <div class="form-item">
                        <div class="form-label">年级</div>
                        <div class="form-input" @click="showGradePicker = true">
                            <span class="input-text" :class="{ placeholder: !formData.grade }">
                                {{ formData.grade ? formData.grade.name : '请选择年级' }}
                            </span>
                            <van-icon name="arrow" class="input-icon" />
                        </div>
                    </div>

                    <!-- 班级 -->
                    <div class="form-item">
                        <div class="form-label">班级</div>
                        <div class="form-input" @click="showClassPicker = true">
                            <span class="input-text" :class="{ placeholder: !formData.class }">
                                {{ formData.class ? formData.class.name : '请选择班级' }}
                            </span>
                            <van-icon name="arrow" class="input-icon" />
                        </div>
                    </div>

                    <!-- 学生姓名 -->
                    <div class="form-item">
                        <div class="form-label">学生姓名</div>
                        <van-field v-model="formData.studentName" placeholder="请输入学生姓名" class="custom-field" />
                    </div>

                    <!-- 家长电话 -->
                    <div class="form-item">
                        <div class="form-label">家长电话</div>
                        <van-field v-model="formData.parentPhone" placeholder="请输入家长联系电话" type="tel" class="custom-field" />
                    </div>

                    <!-- 学校每日限购瓶数 -->
                    <!-- <div class="form-item">
                        <div class="form-label">学校每日限购瓶数</div>
                        <van-field v-model="formData.schoolLimit" placeholder="请输入学校每日限购瓶数" type="number" class="custom-field" />
                    </div> -->

                    <!-- 社区每日限购瓶数 -->
                    <!-- <div class="form-item">
                        <div class="form-label">社区每日限购瓶数</div>
                        <div class="currency-input-wrapper">
                            <van-field v-model="formData.communityLimit" placeholder="请输入社区每日限购瓶数" type="number"
                                class="custom-field" />
                        </div>
                    </div> -->
                </van-form>
                <!-- 按钮区域 -->
                <div class="modal-actions">
                    <van-button type="default" size="large" class="cancel-btn" @click="handleCancel">
                        取消
                    </van-button>
                    <van-button type="primary" size="large" class="confirm-btn" @click="handleSubmit" :disabled="!isFormComplete">
                        确认
                    </van-button>
                </div>
            </div>
        </div>
    </van-popup>

    <!-- 学校选择器（支持搜索） -->
    <SearchablePicker v-model:show="showSchoolPicker" title="选择学校" :options="schoolOptions" :value="formData.school"
        value-key="id" display-key="name" @confirm="onSchoolConfirm" @cancel="showSchoolPicker = false" />

    <!-- 年级选择器 -->
    <van-popup v-model:show="showGradePicker" position="bottom" round>
        <van-picker :columns="gradeOptions" @confirm="onGradeConfirm" @cancel="showGradePicker = false" title="选择年级"
            show-toolbar :columns-field-names="customFieldName"/>
    </van-popup>

    <!-- 班级选择器 -->
    <van-popup v-model:show="showClassPicker" position="bottom" round>
        <van-picker :columns="classOptions" @confirm="onClassConfirm" @cancel="showClassPicker = false" title="选择班级"
            show-toolbar :columns-field-names="customFieldName"/>
    </van-popup>
</template>

<script setup>
import { ref, reactive, defineProps, defineEmits, onMounted, computed, watch } from 'vue'
import SearchablePicker from './SearchablePicker.vue'
import { Toast, Dialog } from 'vant'
import { post } from '../utils/request.js'

// Props
const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    mode: {
        type: String,
        default: 'add', // 'add' 或 'edit'
        validator: (value) => ['add', 'edit'].includes(value)
    },
    editData: {
        type: Object,
        default: null
    }
})

// 定义 emit
const emit = defineEmits(['update:show', 'submit'])

// 响应式数据
const formData = reactive({
    school: null,
    grade: null,
    class: null,
    studentName: '',
    parentPhone: '',
    schoolLimit: '2',
    communityLimit: '2'
})

// 选择器显示状态
const showSchoolPicker = ref(false)
const showGradePicker = ref(false)
const showClassPicker = ref(false)

// 选项数据
const schoolOptions = ref([])
const gradeOptions = ref([])
const classOptions = ref([])
const customFieldName = {
    text: 'name',
    value: 'id'
}

// 检查表单是否完整
const isFormComplete = computed(() => {
    return formData.school && 
           formData.grade && 
           formData.class && 
           formData.studentName.trim() && 
           formData.parentPhone.trim()
})

// 获取学校列表
const fetchSchoolOptions = async () => {
    try {
        const response = await post('/api/select/child/partner/place', {
            company_ids: JSON.parse(localStorage.getItem('company_ids')),
        })
        if (response.result && response.result.state === 'ok') {
            schoolOptions.value = response.result.place_arr || []
        } else {
            Toast('获取学校列表失败')
        }
    } catch (error) {
        console.error('获取学校列表失败:', error)
        Toast('获取学校列表失败')
    }
}

// 组件挂载时获取数据
onMounted(() => {
    fetchSchoolOptions()
})

// 监听编辑数据变化，填充表单
const fillEditData = () => {
    if (props.mode === 'edit' && props.editData) {
        // 重置表单数据
        resetForm()
        
        // 查找对应的学校、年级、班级对象
        if (props.editData.school_id && schoolOptions.value.length > 0) {
            const school = schoolOptions.value.find(s => s.id === props.editData.school_id)
            if (school) {
                formData.school = school
                gradeOptions.value = school.common_grade_arr || []
                
                if (props.editData.grade_id && gradeOptions.value.length > 0) {
                    const grade = gradeOptions.value.find(g => g.id === props.editData.grade_id)
                    if (grade) {
                        formData.grade = grade
                        classOptions.value = grade.class_arr || []
                        
                        if (props.editData.class_id && classOptions.value.length > 0) {
                            const classItem = classOptions.value.find(c => c.id === props.editData.class_id)
                            if (classItem) {
                                formData.class = classItem
                            }
                        }
                    }
                }
            }
        }
        
        // 填充其他字段
        formData.studentName = props.editData.name || ''
        formData.parentPhone = props.editData.parent_phone || ''
        formData.schoolLimit = props.editData.school_quota_num || '2'
        formData.communityLimit = props.editData.quota_num || '2'
    }
}

// 监听编辑数据变化
watch(() => props.editData, fillEditData, { immediate: true })

// 方法
const handleClose = () => {
    emit('update:show', false)
    resetForm()
}

const handleCancel = () => {
    emit('update:show', false)
    resetForm()
}

const handleUpdateShow = (value) => {
    emit('update:show', value)
}

const handleSubmit = async () => {
    // 提交数据
    try {
        const params = {
            partner_id: parseInt(localStorage.getItem('userId')),
            name: formData.studentName,
            parent_phone: formData.parentPhone,
            school_id: formData.school.id,
            grade_id: formData.grade.id,
            class_id: formData.class.id,
            // school_quota_num: formData.schoolLimit,
            // quota_num: formData.communityLimit,
        }
        if (props.mode === 'edit') {
            params.student_id = props.editData.id
        }
        const res = await post('/api/add/child/student', params)
        if (res.result && res.result.state === 'ok') {
            emit('submit', { ...formData })
            emit('update:show', false)
            resetForm()
            Toast(props.mode === 'edit' ? '修改成功' : '添加成功')
        } else {
            Dialog.alert({
                title: '提示',
                message: res.result?.message || '接口异常'
            })
        }
    } catch (error) {
        console.error(props.mode === 'edit' ? '修改学生失败:' : '添加学生失败:', error)
        Toast(props.mode === 'edit' ? '修改学生失败' : '添加学生失败')
    }
}

const onSchoolConfirm = (value) => {
    formData.school = value
    gradeOptions.value = value.common_grade_arr
    formData.grade = null
    formData.class = null
    showSchoolPicker.value = false
}

const onGradeConfirm = (value) => {
    formData.grade = value
    classOptions.value = value.class_arr
    formData.class = null
    showGradePicker.value = false
}

const onClassConfirm = (value) => {
    formData.class = value
    showClassPicker.value = false
}

const resetForm = () => {
    Object.assign(formData, {
        school: null,
        grade: null,
        class: null,
        studentName: '',
        parentPhone: '',
        schoolLimit: '2',
        communityLimit: '2'
    })
    // 清空选择器选项
    gradeOptions.value = []
    classOptions.value = []
}
</script>

<style scoped>
.add-student-modal {
    padding: 21px 0;
    background: #fff;
}

.modal-header {
    padding: 0 21px;
    margin-bottom: 21px;
}

.modal-title {
    font-size: 16px;
    font-weight: 500;
    color: #000;
}

.modal-content {
    height: 75vh;
    overflow: hidden;
}

.form-container {
    height: 90%;
    overflow-y: auto;
    padding: 0 21px;
}

/* 表单项样式 */
.form-item {
    margin-bottom: 13px;
}

.form-label {
    font-size: 12px;
    color: #4B5563;
    margin-bottom: 5px;
}

.form-input {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 10px;
    border: 1px solid rgba(229, 231, 235, 1);
    border-radius: 7px;
}

.input-text {
    font-size: 12px;
    color: #333;
    flex: 1;
}

.input-text.placeholder {
    color: #6B7280;
}

.input-icon {
    color: #6B7280;
    font-size: 16px;
    margin-left: 8px;
}

/* 自定义字段样式 */
:deep(.custom-field) {
    padding: 0;
    background: transparent;
}

:deep(.custom-field .van-field__control) {
    padding: 5px 10px;
    border: 1px solid rgba(229, 231, 235, 1);
    border-radius: 7px;
    font-size: 12px;
    color: #333;
    -webkit-text-size-adjust: 100%;
}

@supports (-webkit-touch-callout: none) {
    .form-label {
        font-size: 14px;
    }

    .input-text {
        font-size: 16px;
    }

    :deep(.custom-field .van-field__control) {
        font-size: 16px;
    }
}

:deep(.custom-field .van-field__control:focus) {
    border-color: var(--van-primary-color);
    background: #fff;
}


:deep(.custom-field .van-field__control::placeholder) {
    color: #6B7280;
}

:deep(.custom-field .van-field__left-icon) {
    color: #6B7280;
    font-size: 14px;
    font-weight: 500;
}

.currency-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.currency-symbol {
    position: absolute;
    left: 10px;
    z-index: 1;
    color: #6B7280;
    font-size: 14px;
    font-weight: 500;
    pointer-events: none;
}

.modal-actions {
    display: flex;
    gap: 12px;
    padding: 8px 21px 21px;
}

.cancel-btn {
    flex: 1;
    background: #f5f5f5;
    border: none;
    color: #4B5563;
    border-radius: 4px;
    height: 38px;
    font-size: 14px;
}

.confirm-btn {
    flex: 1;
    border: none;
    color: #fff;
    border-radius: 4px;
    height: 38px;
    font-size: 14px;
}

/* 选择器样式 */
:deep(.van-popup) {
    border-radius: 12px;
}

:deep(.van-picker__title) {
    color: #333;
    font-weight: 500;
}

:deep(.van-picker__confirm) {
    color: var(--van-primary-color);
}

:deep(.van-picker__cancel) {
    color: #6B7280;
}
</style>
